import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import { IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  removeUnsupportedFile,
  setEditingAsset,
} from "../../app/slices/AssetsSlice";
import PiPopoverMenu from "../../components/PiPopoverMenu";
import { AssetDetailsDTO, AssetKey } from "../../dto/AssetDTO";
import useDeleteAsset from "../../hooks/DeleteAssetHook";
import PiMenuButton from "../menu/PiMenuButton";

interface AssetRowMenuProps {
  asset: AssetDetailsDTO;
  /** Starts the inline rename. Omitted where there is nowhere to draw it. */
  onRename?: () => void;
}

/**
 * The per-asset actions, shared by the tile and the list row so the two views
 * offer the same verbs from the same place.
 *
 * Localize only names the asset in the slice; the dialog itself is mounted
 * once by the explorer body. It used to live here, which meant every card
 * carried its own copy — and the open one died the moment its card unmounted,
 * as happens on a view switch or when a virtualised row scrolls away.
 *
 * Chrome, not content: hidden until the surrounding card or row is hovered,
 * which is why both roots carry Tailwind's `group`.
 */
export default function AssetRowMenu(props: AssetRowMenuProps) {
  const { asset, onRename } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [deleteAsset] = useDeleteAsset();

  const onLocalize = () => dispatch(setEditingAsset(asset.id));

  // Nothing to offer while the upload is still in flight.
  if (asset.key === AssetKey.Loading) return null;

  return (
    <span className="assetrowmenu-root shrink-0 opacity-0 transition-opacity duration-[120ms] group-hover:opacity-100 focus-within:opacity-100">
      {asset.key === AssetKey.Unsupported ? (
        <IconButton
          color="error"
          onClick={() => dispatch(removeUnsupportedFile(asset.id))}
        >
          <DeleteOutlineOutlinedIcon />
        </IconButton>
      ) : (
        // The same menu treatment the interaction rows and take cards use:
        // rounded card, icon per item, danger tone on the destructive one.
        <PiPopoverMenu softIconHover modernMenu>
          {/* Every item here does something. Preview and Duplicate were
              permanently disabled placeholders, and a menu of mostly-dead
              entries makes the live ones harder to find.

              "Edit" opened a dialog titled "Localize File" and was the only
              place a file could be renamed. Both halves are named now. */}
          <PiMenuButton
            text={t("rename")}
            icon={<EditOutlinedIcon color="inherit" fontSize="inherit" />}
            onClick={onRename}
            disabled={!onRename}
            variant="modern"
          />
          <PiMenuButton
            text={t("localize_ui.menu_label")}
            icon={<TranslateOutlinedIcon color="inherit" fontSize="inherit" />}
            onClick={onLocalize}
            variant="modern"
          />
          <PiMenuButton
            text={t("delete")}
            icon={
              <DeleteOutlineOutlinedIcon color="inherit" fontSize="inherit" />
            }
            onClick={() => deleteAsset(asset.id)}
            variant="modern"
            tone="danger"
          />
        </PiPopoverMenu>
      )}
    </span>
  );
}
